import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  FiHelpCircle,
  FiInfo,
  FiSearch,
  FiChevronDown,
  FiChevronUp,
  FiCopy,
  FiFilter,
} from "react-icons/fi";
import { CgCopy } from "react-icons/cg";
import { Link } from "react-router-dom";

/*
  FAQ – Sistema de Consultas (PF, PJ e Veicular)
  -------------------------------------------------
  - Puro React + Tailwind + Framer Motion
  - Sem dependência de UI lib (funciona em qualquer projeto com Tailwind)
  - Filtro por busca e por categoria
  - Link âncora por pergunta (copiar link)
  - Props para customização e callback de contato

  Uso:
  import Faq from "./Faq";
  <Faq onContactClick={() => openSupportModal()} />
*/

/* ============================ Tipos ============================ */
/* ============================ Helpers ============================ */
function slugify(text) {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

function highlight(text, query) {
  if (!query) return text;
  const parts = text.split(new RegExp(`(${query})`, "ig"));
  return (
    <>
      {parts.map((p, i) => (
        <span
          key={i}
          className={
            p.toLowerCase() === query.toLowerCase()
              ? "bg-yellow-100 rounded px-0.5"
              : undefined
          }
        >
          {p}
        </span>
      ))}
    </>
  );
}

/* ============================ Dados padrão ============================ */
const DEFAULT_SECTIONS = [
  {
    id: "geral",
    label: "Dúvidas Gerais",
    icon: <FiInfo className="h-4 w-4" />,
    items: [
      {
        q: "O que é uma consulta de crédito?",
        a: (
          <p>
            É a pesquisa em bases públicas e privadas para avaliar risco de
            concessão de crédito ou o histórico financeiro de uma pessoa (PF) ou
            empresa (PJ). Consolidamos dados de diferentes fontes e apresentamos
            um resumo com quantidades, valores e indicadores quando disponíveis.
          </p>
        ),
        tags: ["score", "pf", "pj", "relatório"],
      },
      {
        q: "Consultar impacta o score?",
        a: (
          <p>
            Consultas feitas dentro do nosso sistema são do tipo soft e não
            impactam o score do consumidor. Já solicitações de crédito em
            instituições (hard) podem causar variações temporárias.
          </p>
        ),
        tags: ["score", "impacto"],
      },
      {
        q: "De onde vêm as informações?",
        a: (
          <p>
            Bureaus de crédito, cartórios de protesto, tribunais, órgãos
            públicos e outras bases confiáveis. Não somos o emissor original; a
            correção/baixa deve ser solicitada ao responsável pelo registro
            (credor, cartório, órgão etc.).
          </p>
        ),
        tags: ["fontes", "oficial", "atualização"],
      },
      {
        q: "Por quanto tempo uma negativação permanece?",
        a: (
          <p>
            Registros de inadimplência podem permanecer por até 5 anos a partir
            do vencimento da dívida (observadas as normas aplicáveis). Depois
            disso, deixam de ser exibidos.
          </p>
        ),
        tags: ["prazo", "negativação"],
      },
      {
        q: "Paguei a dívida, por que ainda aparece?",
        a: (
          <p>
            Após a quitação, o credor/cartório precisa processar a baixa. O
            reflexo nas bases pode levar de algumas horas a alguns dias úteis.
            Guarde o comprovante e, se necessário, envie evidências no suporte.
          </p>
        ),
        tags: ["baixa", "prazo", "atualização"],
      },
      {
        q: "Posso consultar sem consentimento? (LGPD)",
        a: (
          <p>
            É necessário ter base legal (consentimento do titular ou legítimo
            interesse com finalidade legítima e proporcional). Registre e
            mantenha evidências de consentimento quando aplicável e use as
            consultas apenas para finalidades permitidas.
          </p>
        ),
        tags: ["lgpd", "consentimento", "base legal"],
      },
      {
        q: "Qual a diferença entre PF e PJ no relatório?",
        a: (
          <p>
            PF enfatiza score do consumidor, pendências, protestos e CCF; PJ
            traz dados societários, processos, duplicatas, concentração de
            risco, recuperação judicial/falência.
          </p>
        ),
        tags: ["pf", "pj", "diferenças"],
      },
      {
        q: "O que significa “sem ocorrência”?",
        a: (
          <p>
            Que não localizamos registros daquele tipo no escopo pesquisado.
            Isso não substitui certidões oficiais nem garante inexistência fora
            do escopo.
          </p>
        ),
        tags: ["sem ocorrência", "escopo"],
      },
    ],
  },

  {
    id: "credito-pf",
    label: "Crédito PF",
    icon: <FiHelpCircle className="h-4 w-4" />,
    items: [
      {
        q: "O que é pendência financeira?",
        a: (
          <div className="space-y-2">
            <p>
              Débito vencido e não pago informado por um credor (ex.:
              empréstimos, cartões, financiamentos, serviços). Exibimos a
              quantidade e o <strong>valor total</strong> informado.
            </p>
            <p className="text-sm text-slate-600">
              <strong>Como regularizar:</strong> negocie, quite e solicite a
              baixa ao credor. O reflexo nas bases pode levar alguns dias úteis.
            </p>
          </div>
        ),
        tags: ["pendência", "dívida", "atraso"],
      },
      {
        q: "O que é protesto? Como dar baixa?",
        a: (
          <div className="space-y-2">
            <p>
              É o registro em cartório de dívida não paga. Para baixa: quite a
              dívida, obtenha a carta de anuência e protocole no cartório do
              protesto para retirar o registro.
            </p>
            <p className="text-sm text-slate-600">
              O prazo de baixa varia por cartório/UF.
            </p>
          </div>
        ),
        tags: ["protesto", "cartório", "baixa"],
      },
      {
        q: "O que é CCF (cheque sem fundo)?",
        a: (
          <div className="space-y-2">
            <p>
              Cadastro de Emitentes de Cheques sem Fundos. Para exclusão, quite
              o débito com o banco/beneficiário e solicite a retirada do
              registro.
            </p>
          </div>
        ),
        tags: ["cheque", "ccf"],
      },
      {
        q: "Score de crédito: como interpretar?",
        a: (
          <div className="space-y-2">
            <p>
              Pontuação (0–1000) que estima probabilidade de pagamento futuro.
              Varia com novas informações e políticas dos bureaus. Não garante
              aprovação.
            </p>
            <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
              <li>Consultas internas (soft) não reduzem o score.</li>
              <li>Varia com tempo, relacionamento, novas dívidas e baixas.</li>
            </ul>
          </div>
        ),
        tags: ["score", "pontuação"],
      },
      {
        q: "Histórico de consultas (quem consultou meu CPF)?",
        a: (
          <p>
            Mostra instituições que consultaram seu CPF no período. Muitas
            consultas em curto prazo podem sinalizar risco; o impacto depende da
            política de cada bureau.
          </p>
        ),
        tags: ["histórico", "consultas"],
      },
      {
        q: "Fraude/identidade: o que fazer se houver suspeita?",
        a: (
          <p>
            Registre boletim de ocorrência, comunique bancos/lojas envolvidos,
            conteste junto ao credor e abra chamado no suporte. Guarde
            documentos e protocolos.
          </p>
        ),
        tags: ["fraude", "identidade", "disputa"],
      },
      {
        q: "Paguei, mas o valor ainda aparece diferente",
        a: (
          <p>
            Pode haver atualização, juros/correção ou arredondamentos aplicados
            pela origem. Envie comprovantes para conferência.
          </p>
        ),
        tags: ["valor", "diferença"],
      },
    ],
  },

  {
    id: "credito-pj",
    label: "Crédito PJ",
    icon: <FiHelpCircle className="h-4 w-4" />,
    items: [
      {
        q: "Pendência financeira PJ funciona igual à PF?",
        a: (
          <p>
            Sim. Em PJ são comuns duplicatas, contratos comerciais e obrigações
            empresariais. A baixa ocorre após quitação e solicitação do credor
            às bases.
          </p>
        ),
        tags: ["pj", "pendência"],
      },
      {
        q: "Ações/Execuções: como interpretar?",
        a: (
          <p>
            Processos em que a empresa é parte. O relatório pode exibir classe,
            valor da causa e status. O valor da causa não é necessariamente
            dívida líquida.
          </p>
        ),
        tags: ["ação judicial", "processo"],
      },
      {
        q: "Recuperação judicial x falência",
        a: (
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>Recuperação Judicial</strong>: reorganização para pagar
              credores.
            </li>
            <li>
              <strong>Falência</strong>: liquidação com administrador judicial.
            </li>
          </ul>
        ),
        tags: ["recuperação", "falência"],
      },
      {
        q: "Concentração de risco em poucos clientes/fornecedores",
        a: (
          <p>
            Alta dependência de poucos parceiros aumenta exposição. Indicadores
            de concentração ajudam na avaliação de risco PJ.
          </p>
        ),
        tags: ["concentração", "riscos"],
      },
      {
        q: "Quadro societário e sócios influenciam?",
        a: (
          <p>
            Mudanças societárias e histórico dos sócios podem influenciar a
            análise. Alguns relatórios exibem vínculos relevantes.
          </p>
        ),
        tags: ["societário", "sócios"],
      },
      {
        q: "Faturamento presumido e segmentação",
        a: (
          <p>
            Quando disponível, estimativas de faturamento e indicadores
            comportamentais apoiam a avaliação, mas não substituem documentos
            contábeis oficiais.
          </p>
        ),
        tags: ["faturamento", "indicadores"],
      },
    ],
  },

  {
    id: "veicular",
    label: "Veicular",
    icon: <FiHelpCircle className="h-4 w-4" />,
    items: [
      {
        q: "Ocorrência de leilão ou sinistro",
        a: (
          <p>
            Indica que o veículo pode ter passado por leilão/sinistro (perda
            parcial/total), afetando valor e risco. Verifique laudos, fotos e
            documentação antes da compra.
          </p>
        ),
        tags: ["leilão", "sinistro"],
      },
      {
        q: "Base estadual x base nacional (DETRAN/BIN)",
        a: (
          <p>
            <strong>Estadual (DETRAN/UF)</strong>: registros locais (multas,
            restrições, licenciamento). <strong>BIN</strong>: dados agregados
            nacionais. A frequência de sincronização varia por estado.
          </p>
        ),
        tags: ["detran", "bin", "base"],
      },
      {
        q: "Gravame/alienação e restrições",
        a: (
          <p>
            Indicam financiamento ativo ou impedimentos à transferência.
            Regularize com o credor e aguarde a baixa antes de transferir.
          </p>
        ),
        tags: ["gravame", "alienação", "restrição"],
      },
      {
        q: "Roubo/furto e recuperação",
        a: (
          <p>
            Registros podem aparecer com data e status. Mesmo recuperado, o
            histórico pode impactar valor de mercado e seguro.
          </p>
        ),
        tags: ["roubo", "furto"],
      },
      {
        q: "CRLV-e, licenciamento e multas",
        a: (
          <p>
            Informações de documentação e infrações podem constar nas bases
            estaduais. Regularize pendências para evitar impedimentos.
          </p>
        ),
        tags: ["crlv-e", "licenciamento", "multas"],
      },
      {
        q: "Chassi remarcado, bloqueios e recalls",
        a: (
          <p>
            Quando disponível, exibimos observações relevantes. Consulte laudos
            e verifique junto ao DETRAN/ fabricantes sobre recalls.
          </p>
        ),
        tags: ["chassi", "recall", "bloqueio"],
      },
    ],
  },

  {
    id: "operacional",
    label: "Operacional & Relatório",
    icon: <FiHelpCircle className="h-4 w-4" />,
    items: [
      {
        q: "Como é calculado o ‘valor total’?",
        a: (
          <p>
            Somatório dos valores informados para cada tipo de ocorrência
            (pendências, protestos etc.). Podem existir diferenças por
            atualização, juros/correção e arredondamentos.
          </p>
        ),
        tags: ["valor total", "somatório"],
      },
      {
        q: "Quando as baixas aparecem no relatório?",
        a: (
          <p>
            Depende da fonte. Algumas atualizam em 24–72h; outras têm fluxos
            manuais (ex.: cartórios). Envie comprovantes pelo suporte para
            acompanharmos o espelhamento.
          </p>
        ),
        tags: ["atualização", "prazo"],
      },
      {
        q: "O que é ‘data da maior pendência’?",
        a: (
          <p>
            A data mais recente (ou relevante) dentre as ocorrências
            encontradas. Ajuda a identificar a recência do risco.
          </p>
        ),
        tags: ["data", "pendência"],
      },
      {
        q: "Por que relatórios diferentes mostram números diferentes?",
        a: (
          <p>
            Fornecedores possuem coberturas e janelas de atualização distintas.
            Em alguns casos usamos múltiplos provedores; variações podem
            ocorrer.
          </p>
        ),
        tags: ["provedores", "variação"],
      },
      {
        q: "Posso exportar/baixar o relatório em PDF?",
        a: (
          <p>
            Sim, quando disponível no seu plano. Use o botão de exportação/print
            no topo do relatório. Guarde o protocolo da consulta.
          </p>
        ),
        tags: ["pdf", "exportação", "protocolo"],
      },
    ],
  },

  {
    id: "financeiro",
    label: "Financeiro & Planos",
    icon: <FiHelpCircle className="h-4 w-4" />,
    items: [
      {
        q: "Como funciona a cobrança das consultas?",
        a: (
          <p>
            Depende do seu plano (pré-pago por crédito, pacote, ou pós-pago).
            Cada tipo de consulta tem preço unitário e consome saldo/créditos
            conforme contratado.
          </p>
        ),
        tags: ["preço", "plano", "créditos"],
      },
      {
        q: "Formas de pagamento",
        a: (
          <p>
            Pix e boleto (entre outras quando habilitadas). Após compensação, o
            saldo e as liberações são atualizados no painel.
          </p>
        ),
        tags: ["pagamento", "pix", "boleto"],
      },
      {
        q: "Notas fiscais e recibos",
        a: (
          <p>
            Disponíveis no painel financeiro quando aplicável. Baixe os
            documentos para conciliação.
          </p>
        ),
        tags: ["nota fiscal", "recibo"],
      },
      {
        q: "Créditos expiram?",
        a: (
          <p>
            Consulte as regras do seu plano. Alguns créditos têm validade;
            outros não expiram durante o contrato ativo.
          </p>
        ),
        tags: ["créditos", "validade"],
      },
      {
        q: "Reembolso e estorno",
        a: (
          <p>
            Analisamos caso a caso conforme contrato e uso efetivo. Abra
            solicitação com detalhes e protocolos.
          </p>
        ),
        tags: ["reembolso", "estorno"],
      },
    ],
  },

  {
    id: "conta",
    label: "Conta & Acesso",
    icon: <FiHelpCircle className="h-4 w-4" />,
    items: [
      {
        q: "Como acessar minha conta?",
        a: (
          <p>
            Acesse com e-mail/senha ou login social (quando habilitado). Em caso
            de erro, use “Esqueci minha senha”.
          </p>
        ),
        tags: ["login", "senha"],
      },
      {
        q: "Permissões por usuário/empresa",
        a: (
          <p>
            Perfis com permissões distintas controlam o que cada usuário pode
            ver e executar. Solicite ajustes ao administrador da sua empresa.
          </p>
        ),
        tags: ["permissões", "acesso"],
      },
      {
        q: "Posso limitar quem vê os relatórios?",
        a: (
          <p>
            Sim, via perfis e papéis (roles). Relatórios podem ser restritos por
            equipe, usuário ou filial.
          </p>
        ),
        tags: ["privacidade", "perfis"],
      },
    ],
  },

  {
    id: "api",
    label: "API & Integrações",
    icon: <FiHelpCircle className="h-4 w-4" />,
    items: [
      {
        q: "Existe API para integrar meu sistema?",
        a: (
          <p>
            Sim, quando previsto no seu contrato. Fornecemos endpoints
            autenticados, documentação e exemplos na área de desenvolvedores.
          </p>
        ),
        tags: ["api", "integração"],
      },
      {
        q: "Limites de uso e rate limit",
        a: (
          <p>
            Há limites por minuto/hora/dia para garantir estabilidade. Precisa
            de mais capacidade? Solicite aumento mediante análise.
          </p>
        ),
        tags: ["rate limit", "limites"],
      },
      {
        q: "Ambiente de testes (sandbox)",
        a: (
          <p>
            Quando disponível, use chaves de teste e dados fictícios. Os
            resultados são simulados e podem diferir do ambiente de produção.
          </p>
        ),
        tags: ["sandbox", "teste"],
      },
    ],
  },

  {
    id: "erros",
    label: "Erros Comuns & Suporte",
    icon: <FiHelpCircle className="h-4 w-4" />,
    items: [
      {
        q: "Erro 401 (não autorizado)",
        a: (
          <p>
            Sessão expirada ou credenciais incorretas. Faça login novamente e
            verifique permissões.
          </p>
        ),
        tags: ["401", "login"],
      },
      {
        q: "Erro 403 (acesso negado)",
        a: (
          <p>
            Falta permissão/plano para o recurso. Fale com o admin da empresa
            para liberar acesso.
          </p>
        ),
        tags: ["403", "permissão"],
      },
      {
        q: "Erro 404 (não encontrado)",
        a: (
          <p>Recurso inexistente ou removido. Confira a URL/ID ou pesquise.</p>
        ),
        tags: ["404", "rota"],
      },
      {
        q: "Erro 422 (validação)",
        a: (
          <p>
            Parâmetros inválidos (ex.: CPF/CNPJ mal formatado). Corrija os
            campos e tente novamente.
          </p>
        ),
        tags: ["422", "validação"],
      },
      {
        q: "Erro 429 (muitas requisições)",
        a: (
          <p>
            O limite de requisições foi atingido. Aguarde e ajuste a cadência
            das chamadas.
          </p>
        ),
        tags: ["429", "rate limit"],
      },
      {
        q: "Erro 500/timeout (fornecedor indisponível)",
        a: (
          <p>
            O provedor pode estar instável. Tente mais tarde. Se persistir, abra
            chamado com protocolo, parâmetro, data/hora e prints.
          </p>
        ),
        tags: ["500", "timeout", "fornecedor"],
      },
      {
        q: "Quais evidências enviar ao suporte?",
        a: (
          <p>
            Protocolo da consulta, prints do relatório, CPF/CNPJ (mascarado se
            necessário), comprovante de quitação, carta de anuência, número de
            cartório/ação, data/hora do erro e e-mail de contato.
          </p>
        ),
        tags: ["suporte", "evidências"],
      },
    ],
  },
];

/* ============================ UI ============================ */
function CategoryChip({ active, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className={
        "px-3 py-1.5 rounded-full border transition-all text-xs flex items-center gap-1 " +
        (active
          ? "bg-blue-600 text-white border-blue-600 shadow"
          : "bg-white text-slate-700 border-slate-200 hover:border-slate-300")
      }
    >
      <FiFilter className="h-3.5 w-3.5 opacity-70" />
      {label}
    </button>
  );
}

function EmptyState({ query }) {
  return (
    <div className="text-center p-10 border border-dashed rounded-xl bg-white">
      <FiHelpCircle className="h-8 w-8 mx-auto mb-2 text-slate-400" />
      <h4 className="font-semibold text-slate-700">Nada encontrado</h4>
      <p className="text-slate-500 text-sm">
        Nenhuma pergunta corresponde a “{query}”. Tente termos diferentes.
      </p>
    </div>
  );
}

function ContactCard({ onContactClick }) {
  return (
    <div className="mt-6 rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-5">
      <div className="flex items-start gap-3">
        <div className="shrink-0 rounded-xl bg-blue-600/10 p-2">
          <FiHelpCircle className="h-5 w-5 text-blue-700" />
        </div>
        <div className="grow">
          <h4 className="font-semibold text-slate-800">
            Não encontrou sua resposta?
          </h4>
          <p className="text-sm text-slate-600">
            Fale com nosso time. Envie evidências (prints, protocolos, cartas de
            anuência) para agilizar a análise.
          </p>
        </div>
        <div className="shrink-0">
          <Link
            to="/support"
            className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition inline-block"
          >
            Abrir suporte
          </Link>
        </div>
      </div>
    </div>
  );
}

function FaqAccordionItem({ id, question, answer, isOpen, onToggle, query }) {
  const Q = typeof question === "string" ? question : "";
  return (
    <div id={id} className="rounded-xl border border-gray-300 bg-white">
      <button
        className="w-full text-left p-2 flex items-center justify-between gap-4"
        aria-controls={`${id}-content`}
        aria-expanded={isOpen}
        onClick={onToggle}
      >
        <div className="flex items-center gap-1">
          <div className="rounded-md bg-blue-600/10 p-1.5">
            <FiHelpCircle className="h-4 w-4 text-blue-700" />
          </div>
          <h3 className="font-medium text-slate-800 leading-6 text-sm">
            {typeof question === "string"
              ? highlight(question, query)
              : question}
          </h3>
        </div>
        <div className="flex items-center gap-3 mr-3">
          {isOpen ? (
            <FiChevronUp className="h-4 w-4" />
          ) : (
            <FiChevronDown className="h-4 w-4" />
          )}
        </div>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            id={`${id}-content`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="overflow-hidden border-t border-slate-200"
          >
            <div className="p-4 text-slate-700 text-sm leading-relaxed">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function CopyLinkButton({ id }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={async (e) => {
        e.stopPropagation();
        const url = `${window.location.origin}${window.location.pathname}#${id}`;
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
      aria-label="Copiar link da pergunta"
    >
      <FiCopy className="h-3.5 w-3.5" />
      {copied ? "Copiado" : "Link"}
    </button>
  );
}

/* ============================ Componente principal ============================ */
export default function Faq({
  title = "Dúvidas Frequentes",
  description = "Encontre respostas rápidas sobre consultas de crédito (PF e PJ) e dados veiculares.",
  initialCategory = "geral",
  sections = DEFAULT_SECTIONS,
  showContactCard = true,
  onContactClick,
  compact = false,
}) {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(initialCategory);
  const [openId, setOpenId] = useState(null);

  // Se há hash na URL, abre a pergunta correspondente
  useEffect(() => {
    const hash = decodeURIComponent(window.location.hash.replace("#", ""));
    if (!hash) return;
    // tenta encontrar em qualquer seção
    sections.forEach((sec) => {
      sec.items.forEach((it) => {
        const id = `faq-${slugify(sec.id)}-${slugify(
          typeof it.q === "string" ? it.q : String(it.q)
        )}`;
        if (id === hash) {
          setActive(sec.id);
          setOpenId(id);
        }
      });
    });
  }, [sections]);

  const filteredSections = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sections
      .filter((sec) => (active ? sec.id === active : true))
      .map((sec) => ({
        ...sec,
        items: q
          ? sec.items.filter((it) => {
              const text =
                (typeof it.q === "string" ? it.q : "") +
                " " +
                (Array.isArray(it.tags) ? it.tags.join(" ") : "");
              return (
                text.toLowerCase().includes(q) ||
                String(it.a).toLowerCase().includes(q)
              );
            })
          : sec.items,
      }));
  }, [sections, active, query]);

  const totalItems = filteredSections.reduce(
    (sum, s) => sum + s.items.length,
    0
  );

  return (
    <section
      className={`${
        compact ? "space-y-3" : "space-y-3"
      } bg-white p-4 rounded-xl shadow-md`}
    >
      {/* Cabeçalho */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2
            className={
              "font-bold text-slate-900 " +
              (compact ? "text-xl" : "text-2xl md:text-3xl")
            }
          >
            {title}
          </h2>
          <p className="text-slate-600 text-sm md:text-base">{description}</p>
        </div>
        <div className="shrink-0 hidden sm:block">
          <div className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
            <FiHelpCircle className="h-4 w-4 text-blue-700" />
            <span className="text-sm text-slate-700">FAQ</span>
          </div>
        </div>
      </div>

      {/* Busca */}
      <div className="relative">
        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Busque por palavra-chave (ex.: protesto, pendência, CCF)"
          className="w-full rounded-xl border border-slate-200 bg-white pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-100"
        />
      </div>

      {/* Categorias */}
      <div className="flex flex-wrap gap-2">
        {sections.map((sec) => (
          <CategoryChip
            key={sec.id}
            active={active === sec.id}
            label={sec.label}
            onClick={() => setActive(sec.id)}
          />
        ))}
      </div>

      {/* Lista */}
      <div className="space-y-3">
        {filteredSections.map((sec) => (
          <div key={sec.id} className="space-y-3">
            {!query && (
              <div className="flex items-center gap-2 mt-6">
                <h3 className="text-base font-semibold tracking-wide text-slate-700 uppercase">
                  {sec.label}
                </h3>
              </div>
            )}

            {sec.items.length === 0 ? (
              <EmptyState query={query} />
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {sec.items.map((it) => {
                  const id = `faq-${slugify(sec.id)}-${slugify(
                    typeof it.q === "string" ? it.q : String(it.q)
                  )}`;
                  const isOpen = openId === id;
                  return (
                    <FaqAccordionItem
                      key={id}
                      id={id}
                      question={it.q}
                      answer={it.a}
                      isOpen={isOpen}
                      onToggle={() => setOpenId(isOpen ? null : id)}
                      query={query}
                    />
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Card de contato */}
      {showContactCard && <ContactCard onContactClick={onContactClick} />}

      {/* Rodapé/Disclaimer */}
      <div className="text-[12px] leading-relaxed text-slate-500">
        <p>
          <strong>Importante:</strong> Este conteúdo tem caráter informativo. As
          definições e prazos podem variar conforme políticas dos fornecedores
          de dados, cartórios e órgãos públicos. Não alteramos registros de
          terceiros; para correções, procure o emissor responsável e mantenha
          comprovantes.
        </p>
      </div>
    </section>
  );
}
